import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Baby, Heart, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';

/**
 * V13.6: EDD is the SOLE input for prenatal users. Trimester + current week
 * are derived from EDD via the standard 280-day rule. Backend recomputes on
 * every read so values can't drift over time.
 */
function deriveFromEdd(eddIso) {
  if (!eddIso) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const edd = new Date(eddIso);
  if (Number.isNaN(edd.getTime())) return null;
  const daysToEdd = Math.round((edd - today) / (1000 * 60 * 60 * 24));
  const weeksRemaining = Math.max(0, Math.floor(daysToEdd / 7));
  const week = Math.max(1, Math.min(42, 40 - weeksRemaining));
  const trimester = week <= 13 ? 1 : week <= 27 ? 2 : 3;
  return { week, trimester, daysToEdd };
}

export default function PregnancyOnboardingModal({ open, onClose, forceMode = false }) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [courseType, setCourseType] = useState('prenatal');
  const [edd, setEdd] = useState('');
  // V14.1 — postnatal-specific fields (both mandatory at first sign-up)
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const pp = user?.pregnancy_profile || {};
    setCourseType(pp.stage || pp.course_type || 'prenatal');
    setEdd(pp.edd || '');
    setDeliveryDate(pp.delivery_date ? String(pp.delivery_date).slice(0, 10) : '');
    setDeliveryMode(pp.delivery_mode || '');
    setStep(1);
  }, [open, user]);

  const derived = deriveFromEdd(edd);

  // EDD bounds: 0–42 weeks ahead. Anything outside is suspicious.
  const today = new Date();
  const minEdd = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000)  // ±2-week tolerance for post-term
                  .toISOString().slice(0, 10);
  const maxEdd = new Date(today.getTime() + 42 * 7 * 24 * 60 * 60 * 1000)
                  .toISOString().slice(0, 10);
  // V14.1 — delivery date bounds: max = today (can't deliver in the future);
  // min = ~2 years ago (after that we don't really call it "postnatal" anymore).
  const todayIso = today.toISOString().slice(0, 10);
  const minDelivery = new Date(today.getTime() - 730 * 24 * 60 * 60 * 1000)
                       .toISOString().slice(0, 10);

  const handleSave = async () => {
    if (courseType === 'prenatal' && !edd) {
      toast.error('Estimated Delivery Date is required to personalize your journey.');
      return;
    }
    if (courseType === 'prenatal' && (!derived || derived.daysToEdd > 42 * 7)) {
      toast.error('Please enter a valid EDD (within the next 42 weeks).');
      return;
    }
    // V14.1 — fresh-postnatal sign-up: delivery_date AND delivery_mode are
    // both mandatory (per OPS V29 + product spec). The legacy "you can add
    // baby's birth date later" copy was misleading — postnatal yoga
    // recommendations depend on weeks-since-delivery, so we need the date now.
    if (courseType === 'postnatal') {
      if (!deliveryDate) {
        toast.error('Please enter your delivery date.');
        return;
      }
      if (!deliveryMode) {
        toast.error('Please choose your delivery mode (Vaginal birth or C-Section).');
        return;
      }
    }
    setSaving(true);
    try {
      if (courseType === 'postnatal') {
        // Use the dedicated postnatal-onboarding endpoint which validates
        // and writes delivery_date + delivery_mode atomically alongside stage.
        await api.post('/api/me/postnatal-onboarding', {
          delivery_date: deliveryDate,
          delivery_mode: deliveryMode,
        });
      } else {
        await api.put('/api/users/me/pregnancy-profile', {
          stage: 'prenatal',
          edd,
        });
      }
      toast.success('Profile saved — your dashboard is personalized now.');
      if (refreshUser) await refreshUser();
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message : (typeof detail === 'string' ? detail : 'Failed to save');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !forceMode) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="pregnancy-onboarding-modal">
        <DialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            {courseType === 'postnatal' ? <Heart className="h-6 w-6 text-primary" /> : <Baby className="h-6 w-6 text-primary" />}
          </div>
          <DialogTitle className="text-center">Welcome, {user?.name?.split(' ')[0]}!</DialogTitle>
          <DialogDescription className="text-center">
            Help us personalize your experience — tips, classes, and guidance tuned to your journey.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3" data-testid="onboarding-step-journey">
            <label className="text-sm font-medium">Where are you in your journey?</label>
            <Select value={courseType} onValueChange={setCourseType}>
              <SelectTrigger data-testid="onboarding-journey-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="prenatal">I&apos;m pregnant (prenatal)</SelectItem>
                <SelectItem value="postnatal">I&apos;ve recently delivered (postnatal)</SelectItem>
                {/* TTC removed V13.14.6 — not launching yet */}
              </SelectContent>
            </Select>
            <DialogFooter>
              {!forceMode && <Button variant="ghost" onClick={onClose}>Later</Button>}
              <Button onClick={() => setStep(2)} data-testid="onboarding-step-1-next">Continue</Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="onboarding-step-week">
            {courseType === 'prenatal' ? (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Estimated Delivery Date <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    min={minEdd}
                    max={maxEdd}
                    value={edd}
                    onChange={(e) => setEdd(e.target.value)}
                    data-testid="onboarding-edd-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Your gynaecologist gave you this date at your first scan. We&apos;ll use it to derive everything else.
                  </p>
                  {derived && (
                    <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm">
                      <p className="font-medium text-emerald-900" data-testid="onboarding-derived-week">
                        Currently <strong>week {derived.week}</strong> · trimester {derived.trimester}
                      </p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        {derived.daysToEdd > 0 ? `${derived.daysToEdd} days to your due date` : 'Welcome to the post-term zone — your gynae may suggest induction soon'}
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : courseType === 'postnatal' ? (
              <>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Delivery date <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="date"
                    min={minDelivery}
                    max={todayIso}
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    data-testid="onboarding-delivery-date-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    The day your baby was born. We&apos;ll use this to recommend the right postnatal yoga and milestones.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Mode of delivery <span className="text-red-500">*</span>
                  </label>
                  <Select value={deliveryMode} onValueChange={setDeliveryMode}>
                    <SelectTrigger data-testid="onboarding-delivery-mode-select">
                      <SelectValue placeholder="Select delivery mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vaginal">Vaginal birth</SelectItem>
                      <SelectItem value="c_section">C-Section</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Yoga &amp; recovery guidance differ between vaginal and C-section deliveries.
                  </p>
                </div>
              </>
            ) : null}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="onboarding-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & continue'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
